import { displayFromCodeableConcept } from '@seen/core/codeableConcept'
import type { ScaleType } from '@seen/core/codings'
import type { ExtendedObservation } from '@seen/core/models/observation'
import type { JSX, MouseEventHandler, ReactNode } from 'react'
import { match } from 'ts-pattern'
import { ObservationWrapper } from '#src/components/rich-text-editor/ObservationNode/ObservationWrapper.tsx'
import { RichText } from '#src/components/RichText.tsx'

export function ObservationDisplay({
  observation,
  codingDisplay,
  scaleType,
  onClick,
  noHover,
}: {
  observation: ExtendedObservation | undefined
  codingDisplay: string
  scaleType: ScaleType | null
  onClick?: MouseEventHandler<HTMLSpanElement>
  noHover?: boolean
}): JSX.Element {
  // If observation has components, display them regardless of scaleType
  if (observation?.hasComponents) {
    return (
      <ObservationComponentDisplay
        observation={observation}
        codingDisplay={codingDisplay}
        onClick={onClick}
        noHover={noHover}
      />
    )
  }

  return match(scaleType)
    .with('Qn', () => (
      <ObservationNumberDisplay
        observation={observation}
        codingDisplay={codingDisplay}
        onClick={onClick}
        noHover={noHover}
      />
    ))
    .with('Ord', 'Nom', () => (
      <ObservationChoiceDisplay
        observation={observation}
        codingDisplay={codingDisplay}
        onClick={onClick}
        noHover={noHover}
      />
    ))
    .with('Nar', () => (
      <ObservationTextDisplay
        observation={observation}
        codingDisplay={codingDisplay}
        onClick={onClick}
        noHover={noHover}
      />
    ))
    .with(null, () => {
      throw new Error('scaleType is required')
    })
    .exhaustive()
}

function ObservationNumberDisplay({
  observation,
  codingDisplay,
  onClick,
  noHover,
}: {
  observation: ExtendedObservation | undefined
  codingDisplay: string
  onClick?: MouseEventHandler<HTMLSpanElement>
  noHover?: boolean
}): JSX.Element {
  return (
    <ObservationDisplayWrapper
      observation={observation}
      codingDisplay={codingDisplay}
      onClick={onClick}
      noHover={noHover}
    >
      {observation?.valueQuantity ?? 'no value'}
      {observation?.valueUnit && (
        <span className="text-foreground-secondary">
          &nbsp;{observation.valueUnit}
        </span>
      )}
      {observation?.noteFirstText && (
        <>
          &nbsp;&nbsp;
          <RichText value={observation.noteFirstText} />
        </>
      )}
    </ObservationDisplayWrapper>
  )
}

function ObservationChoiceDisplay({
  observation,
  codingDisplay,
  onClick,
  noHover,
}: {
  observation: ExtendedObservation | undefined
  codingDisplay: string
  onClick?: MouseEventHandler<HTMLSpanElement>
  noHover?: boolean
}): JSX.Element {
  return (
    <ObservationDisplayWrapper
      observation={observation}
      codingDisplay={codingDisplay}
      onClick={onClick}
      noHover={noHover}
    >
      {displayFromCodeableConcept(observation?.valueCodeableConcept) ??
        'no value'}
      {observation?.noteFirstText && (
        <>
          &nbsp;&nbsp;
          <RichText value={observation.noteFirstText} />
        </>
      )}
    </ObservationDisplayWrapper>
  )
}

function ObservationTextDisplay({
  observation,
  codingDisplay,
  onClick,
  noHover,
}: {
  observation: ExtendedObservation | undefined
  codingDisplay: string
  onClick?: MouseEventHandler<HTMLSpanElement>
  noHover?: boolean
}): JSX.Element {
  return (
    <ObservationDisplayWrapper
      observation={observation}
      codingDisplay={codingDisplay}
      onClick={onClick}
      noHover={noHover}
    >
      <RichText value={observation?.valueString ?? 'no value'} />
    </ObservationDisplayWrapper>
  )
}

function ObservationComponentDisplay({
  observation,
  codingDisplay,
  onClick,
  noHover,
}: {
  observation: ExtendedObservation | undefined
  codingDisplay: string
  onClick?: MouseEventHandler<HTMLSpanElement>
  noHover?: boolean
}): JSX.Element {
  return (
    <ObservationDisplayWrapper
      observation={observation}
      codingDisplay={codingDisplay}
      onClick={onClick}
      noHover={noHover}
    >
      {observation?.componentDisplay ?? 'no value'}
      {observation?.noteFirstText && (
        <>
          &nbsp;&nbsp;
          <RichText value={observation.noteFirstText} />
        </>
      )}
    </ObservationDisplayWrapper>
  )
}

function ObservationDisplayWrapper({
  observation,
  codingDisplay,
  onClick,
  noHover,
  children,
}: {
  observation: ExtendedObservation | undefined
  codingDisplay: string
  onClick?: MouseEventHandler<HTMLSpanElement>
  noHover?: boolean
  children: ReactNode
}): JSX.Element {
  return (
    <ObservationWrapper className="pr-1" onClick={onClick} noHover={noHover}>
      <span>{codingDisplay}:&nbsp;</span>
      {!observation && (
        <span className="animate-pulse text-foreground-tertiary">loading…</span>
      )}
      {observation?.status === 'entered-in-error' && (
        <span className="text-foreground-secondary">deleted</span>
      )}
      {observation && observation.status !== 'entered-in-error' && children}
    </ObservationWrapper>
  )
}
